import { useSecurityOperationContext } from '../../context/SecurityOperationContext';
import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import { previewSecurityResolutionAdd } from '../../utils/ApiCall';
import { parseCloudRunError } from '../msiDriftReviewHelpers';
import {
    CountryChipLabel,
    getCountryChipDisplay,
} from '../shared/securityDisplayPrimitives';
import { MSI_V2_STAGED_IDENTIFIER_COLUMNS } from '../../config/UploadConstant';
import WatchlistTickerBuilder from './WatchlistTickerBuilder';
import AdditionsBasket from './add_security/AdditionsBasket';
import {
    DEFAULT_MAX_SECURITIES,
    EMPTY_PICKS,
    MAX_PICKS_PER_SAVE,
    PICK_OUTCOME,
    addPick,
    hasStalePicks,
    identifierTypeLabel,
    pickFromDiscoveryResult,
    pickFromIdentifierOption,
    remainingCapacity,
    removePick,
    unresolvedPickFromPreview,
} from './add_security/pendingAdditions';
import { stagePicks } from './add_security/stagePicks';
import {
    CountryCodeAutocomplete,
    addDialogButtonSx,
    addDialogChoiceRowSx,
    addDialogCloseButtonSx,
    addDialogContentSx,
    addDialogFieldButtonSx,
    addDialogFieldSx,
    addDialogFooterSx,
    addDialogSelectedChoiceRowSx,
    addDialogSoftCardSx,
    addDialogStaticLabelSx,
    addDialogTabsSx,
    addDialogTitleRowSx,
    addDialogTitleSx,
} from './WatchlistAddSecurityPrimitives';

const IDENTIFIER_PLACEHOLDERS = {
    sedol: 'e.g. 2046251',
    cusip: 'e.g. 037833100',
    isin: 'e.g. US0378331005',
    ticker: 'e.g. AAPL',
    bloomberg_ticker: 'e.g. AAPL US Equity',
    openfigi_figi: 'e.g. BBG000B9Y5X2',
    openfigi_composite: 'e.g. BBG000B9XRY4',
    openfigi_share_class: 'e.g. BBG001S5N8V8',
    barra_localid: 'Enter the Barra LOCALID',
    barrid: 'Enter the Barra BARRID',
    axioma_id: 'Enter the Axioma ID',
};
const STALE_STAGE_MESSAGE = 'One or more security choices are out of date. Remove them and add them again before retrying.';
const REVIEW_UNAVAILABLE_MESSAGE = 'The Watchlist review is unavailable. Refresh and try again.';

function attemptId() {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `watchlist-add-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function reviewAdditionsLabel(count) {
    if (!count) return 'Review additions';
    return `Review ${count} ${count === 1 ? 'addition' : 'additions'}`;
}

function pickName(pick) {
    return pick.country ? `${pick.label} (${pick.country})` : pick.label;
}

// What a pick's outcome tells the user; '' when the basket shows it plainly.
function pickNotice(outcome, pick, before, { announceAdded = false } = {}) {
    switch (outcome) {
        case PICK_OUTCOME.ADDED:
            return announceAdded ? `Added ${pickName(pick)} to the basket.` : '';
        case PICK_OUTCOME.REPLACED:
            return `The basket now has ${pickName(pick)} instead of the earlier listing.`;
        case PICK_OUTCOME.DUPLICATE: {
            const existing = before.find((item) => item.key === pick.key) || pick;
            return `Already in the basket as ${pickName(existing)}.`;
        }
        case PICK_OUTCOME.ALREADY_IN_WATCHLIST:
            return 'That security is already in your Watchlist.';
        case PICK_OUTCOME.WATCHLIST_FULL:
            return 'Your Watchlist is full. Remove a security to add another.';
        case PICK_OUTCOME.SAVE_FIRST:
            return `Review these ${MAX_PICKS_PER_SAVE} additions before adding more.`;
        default:
            return 'That choice is no longer available. Search for it again.';
    }
}

/**
 * Build Watchlist additions: "Search securities" and "Add by identifier" fill
 * one basket, and one Review stages it as one pending Watchlist change (the
 * same staging as the grid's "+ Add security" row). The staged change is
 * handed to the shared Watchlist review, where it is applied or ignored.
 */
export default function WatchlistAddSecurityDialog({
    open,
    onClose,
    expectedWatchlistRevisionId,
    publishedCount = 0,
    onReviewHandoff = null,
}) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const [mode, setMode] = useState('search');
    const [picks, setPicks] = useState(EMPTY_PICKS);
    const [searchCapacity, setSearchCapacity] = useState(null);
    const [notice, setNotice] = useState('');
    const [identifierType, setIdentifierType] = useState('ticker');
    const [identifierValue, setIdentifierValue] = useState('');
    const [requestedCountry, setRequestedCountry] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewCountry, setPreviewCountry] = useState('');
    const [selectedToken, setSelectedToken] = useState('');
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    // The pending change a partly staged Review left ({ revisionId,
    // revisionSeq }); the rest of the basket amends it.
    const [stagedReview, setStagedReview] = useState(null);
    // The newest picks, for a second pick that lands before the next render.
    const picksRef = useRef(picks);
    picksRef.current = picks;
    const attemptsRef = useRef(new Map());
    // A Stage that finishes after the dialog was reset is not shown.
    const stageRunRef = useRef(0);
    const options = useMemo(
        () => (Array.isArray(preview?.options) ? preview.options : []),
        [preview],
    );
    // MSI knows no listing for the identifier: it can still be added as-is and
    // resolved on Apply, the same way an uploaded row is.
    const resolveOnApply = Boolean(preview?.resolve_on_apply_available);
    const capacity = searchCapacity
        ? {
            maxSecurities: searchCapacity.max_securities,
            currentCount: searchCapacity.current_security_count,
        }
        : { maxSecurities: DEFAULT_MAX_SECURITIES, currentCount: publishedCount };
    const stale = hasStalePicks(picks);
    // An emptied basket can still open the change it already staged.
    const openStagedReview = picks.length === 0 && Boolean(stagedReview);
    const canReview = (picks.length > 0 || openStagedReview) && !stale && !busy;
    let reviewLabel = reviewAdditionsLabel(picks.length);
    if (openStagedReview) reviewLabel = 'Open review';
    if (busy === 'stage') reviewLabel = 'Staging…';

    const reset = () => {
        stageRunRef.current += 1;
        setMode('search');
        setPicks(EMPTY_PICKS);
        picksRef.current = EMPTY_PICKS;
        setSearchCapacity(null);
        setNotice('');
        setIdentifierValue('');
        setRequestedCountry('');
        setPreview(null);
        setPreviewCountry('');
        setSelectedToken('');
        setBusy('');
        setError('');
        setStagedReview(null);
        attemptsRef.current = new Map();
    };
    // Hands a staged change to the shared Watchlist review, as the grid does.
    const handOff = (staged) => {
        onReviewHandoff({
            origin: 'watchlist_addition',
            ...staged,
            status: 'changes_awaiting_review',
            sessionScopeToken,
        });
        reset();
        onClose?.();
    };
    const close = () => {
        if (busy) return;
        // Like the grid, a change already staged goes to its review.
        if (stagedReview) {
            handOff(stagedReview);
            return;
        }
        reset();
        onClose?.();
    };

    const addToBasket = (pick, noticeOptions) => {
        const before = picksRef.current;
        const { picks: after, outcome } = addPick(before, pick, capacity);
        if (after !== before) {
            picksRef.current = after;
            setPicks(after);
        }
        setError('');
        setNotice(pickNotice(outcome, pick, before, noticeOptions));
        return outcome;
    };

    const pickSearchResult = (result) => {
        if (busy) return PICK_OUTCOME.INVALID;
        if (result?.already_active) {
            setNotice(pickNotice(PICK_OUTCOME.ALREADY_IN_WATCHLIST));
            return PICK_OUTCOME.ALREADY_IN_WATCHLIST;
        }
        return addToBasket(pickFromDiscoveryResult(result));
    };

    const removeFromBasket = (key) => {
        if (busy) return;
        const after = removePick(picksRef.current, key);
        picksRef.current = after;
        setPicks(after);
        setNotice('');
        if (!hasStalePicks(after)) setError('');
    };

    const resolve = async () => {
        const normalizedValue = identifierValue.trim();
        const normalizedTicker = identifierType === 'ticker' ? normalizedValue : '';
        if (!normalizedValue) {
            setError('Enter the security identifier.');
            return;
        }
        const country = requestedCountry.trim().toUpperCase();
        setBusy('resolve');
        setError('');
        setNotice('');
        const response = await previewSecurityResolutionAdd({
            identifierType,
            identifierValue: normalizedValue,
            ticker: normalizedTicker || undefined,
            requestedCountry: country || undefined,
        });
        setBusy('');
        if (response?.success === false) {
            setError(parseCloudRunError(response?.message || response).message
                || 'Could not resolve this security.');
            return;
        }
        setPreview(response);
        setPreviewCountry(country);
        const selectable = (response?.options || []).filter((option) => option?.selectable);
        setSelectedToken(selectable.length === 1 ? selectable[0].option_token : '');
        if (!selectable.length && !response?.resolve_on_apply_available) {
            setError(response?.message || 'None of these listings can be added.');
        }
    };

    const addIdentifierToBasket = () => {
        if (busy || !preview) return;
        const pick = resolveOnApply
            ? unresolvedPickFromPreview(preview, { requestedCountry: previewCountry })
            : pickFromIdentifierOption(
                preview,
                options.find((option) => option.option_token === selectedToken),
            );
        const outcome = addToBasket(pick, { announceAdded: true });
        if (outcome === PICK_OUTCOME.ADDED || outcome === PICK_OUTCOME.REPLACED) {
            setIdentifierValue('');
            setPreview(null);
            setSelectedToken('');
        }
    };

    const review = async () => {
        if (!canReview) return;
        if (openStagedReview) {
            handOff(stagedReview);
            return;
        }
        if (!sessionScopeToken || typeof onReviewHandoff !== 'function') {
            setError(REVIEW_UNAVAILABLE_MESSAGE);
            return;
        }
        stageRunRef.current += 1;
        const run = stageRunRef.current;
        setBusy('stage');
        setError('');
        setNotice('');
        const outcome = await stagePicks(picksRef.current, {
            expectedWatchlistRevisionId: expectedWatchlistRevisionId || undefined,
            sessionScopeToken,
            attempts: attemptsRef.current,
            newAttemptId: attemptId,
            isCurrent: () => stageRunRef.current === run,
        });
        if (!outcome) return;
        setBusy('');
        picksRef.current = outcome.picks;
        setPicks(outcome.picks);
        // The last change staged, by this run or an earlier partial one.
        const staged = outcome.review || stagedReview;
        if (staged && !outcome.picks.length) {
            handOff(staged);
            return;
        }
        setStagedReview(staged);
        if (outcome.failure) setError(outcome.failure);
        else if (hasStalePicks(outcome.picks)) setError(STALE_STAGE_MESSAGE);
        if (staged) {
            setNotice('The other additions are staged in your pending Watchlist change. '
                + 'Review the rest to add them to it; closing this dialog opens its review.');
        } else if (outcome.notice) {
            setNotice(outcome.notice);
        }
    };

    const exactAddControls = (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Enter one complete identifier, then add the listing to the basket. SEDOL,
                CUSIP, ISIN and ticker show the listings MSI knows; anything else is looked
                up when you apply.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
                <TextField
                    select
                    label="Identifier"
                    value={identifierType}
                    disabled={Boolean(busy)}
                    onChange={(event) => {
                        setIdentifierType(event.target.value);
                        setPreview(null);
                        setSelectedToken('');
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        notched: false,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SellOutlinedIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={[
                        addDialogFieldSx,
                        addDialogStaticLabelSx,
                        { flex: { xs: '1 1 100%', sm: '0 0 260px' } },
                    ]}
                >
                    {MSI_V2_STAGED_IDENTIFIER_COLUMNS.map((value) => (
                        <MenuItem key={value} value={value}>{identifierTypeLabel(value)}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Identifier value"
                    placeholder={IDENTIFIER_PLACEHOLDERS[identifierType]}
                    value={identifierValue}
                    disabled={Boolean(busy)}
                    onChange={(event) => {
                        setIdentifierValue(event.target.value);
                        setPreview(null);
                        setSelectedToken('');
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        notched: false,
                        startAdornment: (
                            <InputAdornment position="start">
                                <TagRoundedIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={[
                        addDialogFieldSx,
                        addDialogStaticLabelSx,
                        { flex: { xs: '1 1 100%', sm: '3 1 220px' } },
                    ]}
                />
                <CountryCodeAutocomplete
                    label="Country (optional)"
                    placeholder="Any country"
                    labelAbove
                    value={requestedCountry}
                    disabled={Boolean(busy)}
                    onChange={(nextCountry) => {
                        setRequestedCountry(nextCountry);
                        setPreview(null);
                        setSelectedToken('');
                    }}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}
                />
                <Button
                    variant="outlined"
                    onClick={() => { void resolve(); }}
                    disabled={Boolean(busy)}
                    startIcon={busy === 'resolve'
                        ? <CircularProgress size={14} />
                        : <SearchRoundedIcon />}
                    sx={[addDialogFieldButtonSx, { flex: { xs: '1 1 100%', sm: '0 0 auto' } }]}
                >
                    Find security
                </Button>
            </Box>
            {options.length > 0 && (
                <Paper variant="outlined" sx={[addDialogSoftCardSx, { p: 0, overflow: 'hidden' }]}>
                    <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 700 }}>
                        Country choices
                    </Typography>
                    <Stack divider={<Divider flexItem />} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                        {options.map((option) => {
                            const checked = selectedToken === option.option_token;
                            const disabled = !option.selectable || Boolean(busy);
                            return (
                                <Box
                                    key={option.option_token}
                                    component="label"
                                    sx={[
                                        addDialogChoiceRowSx,
                                        checked && addDialogSelectedChoiceRowSx,
                                        disabled && { cursor: 'default' },
                                    ]}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={() => {
                                            setSelectedToken(checked ? '' : option.option_token);
                                        }}
                                    />
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{ minWidth: 72, fontWeight: 700 }}
                                    >
                                        {option.country
                                            ? <CountryChipLabel display={getCountryChipDisplay(option.country)} />
                                            : 'Country unavailable'}
                                    </Typography>
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {option.reason || ''}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Stack>
                </Paper>
            )}
            {resolveOnApply && (
                <Alert severity="info">
                    {`${identifierTypeLabel(preview?.identifier_type)} `
                        + `${preview?.identifier_value_norm || ''}`
                        + `${previewCountry ? ` in ${previewCountry}` : ''}`
                        + ' will be looked up when you apply. '
                        + 'If it cannot be matched, it will wait for you in Security Resolution.'}
                </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={<AddRoundedIcon />}
                    onClick={addIdentifierToBasket}
                    disabled={!preview || (!resolveOnApply && !selectedToken) || Boolean(busy)}
                    sx={addDialogButtonSx}
                >
                    Add to basket
                </Button>
            </Box>
        </Stack>
    );

    return (
        <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
            <Box sx={addDialogTitleRowSx}>
                <DialogTitle sx={addDialogTitleSx}>Build Watchlist additions</DialogTitle>
                <IconButton
                    aria-label="Close dialog"
                    onClick={close}
                    disabled={Boolean(busy)}
                    sx={addDialogCloseButtonSx}
                >
                    <CloseRoundedIcon />
                </IconButton>
            </Box>
            <DialogContent sx={addDialogContentSx}>
                {error && <Alert severity="warning">{error}</Alert>}
                <Tabs
                    value={mode}
                    onChange={(_event, nextMode) => {
                        if (busy) return;
                        setMode(nextMode);
                        setNotice('');
                    }}
                    aria-label="Choose how to add Watchlist securities"
                    sx={addDialogTabsSx}
                >
                    <Tab
                        value="search"
                        icon={<SearchRoundedIcon fontSize="small" />}
                        iconPosition="start"
                        label="Search securities"
                    />
                    <Tab
                        value="exact"
                        icon={<SellOutlinedIcon fontSize="small" />}
                        iconPosition="start"
                        label="Add by identifier"
                    />
                </Tabs>
                {mode === 'search' ? (
                    <WatchlistTickerBuilder
                        active={open}
                        busy={Boolean(busy)}
                        picks={picks}
                        remainingCapacity={remainingCapacity(picks, capacity)}
                        onPick={pickSearchResult}
                        onCapacityChange={setSearchCapacity}
                    />
                ) : exactAddControls}
                {notice && (
                    <Alert severity="info" role="status" aria-live="polite">
                        {notice}
                    </Alert>
                )}
                <AdditionsBasket
                    picks={picks}
                    capacity={searchCapacity}
                    busy={Boolean(busy)}
                    onRemove={removeFromBasket}
                />
            </DialogContent>
            <DialogActions sx={addDialogFooterSx}>
                <Button
                    variant="outlined"
                    onClick={close}
                    disabled={Boolean(busy)}
                    sx={addDialogButtonSx}
                >
                    {stagedReview ? 'Close' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => { void review(); }}
                    disabled={!canReview}
                    startIcon={busy === 'stage'
                        ? <CircularProgress size={14} color="inherit" />
                        : null}
                    sx={addDialogButtonSx}
                >
                    {reviewLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

WatchlistAddSecurityDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    expectedWatchlistRevisionId: PropTypes.string,
    // Securities in the published Watchlist, for capacity until a search
    // reports the server's.
    publishedCount: PropTypes.number,
    // The shared Watchlist review: receives the staged change.
    onReviewHandoff: PropTypes.func,
};
